/**
 * LegalInclusionPage — public "add a person" request form (2026-08-15).
 *
 * Reachable at `/legal/inclusion` (no auth, mirrors LegalTakedownPage).
 * Users nominate a person for the draftable catalogue; submissions are
 * stored in `inclusion_requests`, counted per person (typo-tolerant) in
 * the admin Inclusions panel, and emailed to contact@gazumbagames.com.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, CheckCircle2, AlertCircle, Copy, UserPlus } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import TurnstileWidget from '../components/TurnstileWidget';

const CATEGORY_OPTIONS = [
  { value: 'country_leader', label: 'Ruler (president, PM, monarch)' },
  { value: 'ceo',            label: 'CEO / business leader' },
  { value: 'actor',          label: 'Actor' },
  { value: 'athlete',        label: 'Athlete' },
  { value: 'musician',       label: 'Artist / musician' },
  { value: 'influencer',     label: 'Influencer / creator' },
];

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <div className="text-sm font-medium mb-1 flex items-center gap-1.5">
        <span style={{ fontFamily: 'var(--font-heading)' }}>{label}</span>
        {required && <span className="text-destructive">*</span>}
      </div>
      {hint && <div className="text-xs text-muted-foreground mb-1.5">{hint}</div>}
      {children}
    </label>
  );
}

const inputClass =
  'w-full px-3 py-2 rounded-md border border-border/60 bg-card/60 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 transition';

export default function LegalInclusionPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    person_name: '',
    category: 'influencer',
    handles: '',
    website: '',
    reason: '',
    your_email: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const turnstileEnabled = Boolean((process.env.REACT_APP_TURNSTILE_SITE_KEY || '').trim());

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.person_name || !form.category) {
      setError('Please fill the required fields.');
      return;
    }
    if (turnstileEnabled && !turnstileToken) {
      setError('Please complete the bot-verification challenge.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const payload = { ...form };
      if (!payload.your_email) delete payload.your_email;
      const { data } = await api.post('/legal/inclusion', payload, {
        headers: turnstileToken ? { 'X-Turnstile-Token': turnstileToken } : {},
      });
      setReceipt(data);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        'Submission failed. Please email contact@gazumbagames.com directly.';
      setError(typeof msg === 'string' ? msg : 'Submission failed.');
      setTurnstileToken(null);
      setTurnstileKey((k) => k + 1);
    } finally {
      setSubmitting(false);
    }
  };

  if (receipt) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-xl mx-auto pt-12">
          <CheckCircle2 className="h-12 w-12 text-[hsl(143,71%,45%)] mb-4" />
          <h1 className="text-3xl font-bold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
            Suggestion received
          </h1>
          <p className="text-sm text-muted-foreground mb-6 leading-relaxed">{receipt.message}</p>
          <div className="rounded-xl border border-border/60 bg-card/50 p-4 mb-6">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
              Your reference number
            </div>
            <div className="flex items-center justify-between gap-3">
              <code className="text-2xl font-bold tracking-widest" style={{ fontFamily: 'var(--font-heading)' }} data-testid="inclusion-reference">
                {receipt.reference}
              </code>
              <button
                onClick={() => { navigator.clipboard.writeText(receipt.reference); toast.success('Copied'); }}
                className="p-2 hover:bg-secondary/60 rounded transition-colors"
                aria-label="Copy reference"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
          </div>
          <Button onClick={() => navigate('/')} variant="outline" data-testid="inclusion-home">
            Return to home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6" data-testid="legal-inclusion-page">
      <div className="max-w-xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
          data-testid="inclusion-back"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <h1 className="text-3xl sm:text-4xl font-bold mb-2 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
          <UserPlus className="h-8 w-8" /> Request a new person
        </h1>
        <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
          Missing someone you'd love to draft? Tell us who. We track every
          request and add the most-wanted people to the catalogue.
        </p>

        <form onSubmit={submit} className="space-y-5">
          <Field label="Person's name" required>
            <input
              required type="text" value={form.person_name} onChange={update('person_name')}
              className={inputClass} maxLength={200} placeholder="e.g. Jensen Huang"
              data-testid="inc-person-name"
            />
          </Field>

          <Field label="Category" required>
            <select value={form.category} onChange={update('category')} className={inputClass} data-testid="inc-category">
              {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>

          <Field label="Social handle(s)" hint="X / Instagram / TikTok / YouTube — helps us verify and track them.">
            <input
              type="text" value={form.handles} onChange={update('handles')}
              className={inputClass} maxLength={300} placeholder="@handle, youtube.com/@channel…"
              data-testid="inc-handles"
            />
          </Field>

          <Field label="Website" hint="Official site, Wikipedia page, or similar (optional).">
            <input
              type="url" value={form.website} onChange={update('website')}
              className={inputClass} maxLength={500} placeholder="https://…"
              data-testid="inc-website"
            />
          </Field>

          <Field label="Why should they be added?" hint="What makes them fantasy-worthy? Newsworthiness helps them score points.">
            <textarea
              rows={4} value={form.reason} onChange={update('reason')} maxLength={2000}
              className={inputClass} data-testid="inc-reason"
            />
          </Field>

          <Field label="Your email" hint="Optional — only used if we need to follow up. Not displayed publicly.">
            <input
              type="email" value={form.your_email} onChange={update('your_email')}
              className={inputClass} data-testid="inc-email" placeholder="Optional"
            />
          </Field>

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-md border border-destructive/40 bg-destructive/10 text-destructive text-sm" data-testid="inc-error">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {turnstileEnabled && <TurnstileWidget key={turnstileKey} onToken={setTurnstileToken} />}

          <Button
            type="submit"
            disabled={submitting || (turnstileEnabled && !turnstileToken)}
            className="w-full"
            data-testid="inc-submit"
          >
            <Send className="h-4 w-4 mr-2" />
            {submitting ? 'Submitting…' : 'Submit inclusion request'}
          </Button>
        </form>
      </div>
    </div>
  );
}
